import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  FormFieldError,
  FormFieldErrorSmall,
  ToggleOn,
  CircleLoaderCheck,
  CircleLoaderCheckSmall,
} from '@wix/wix-ui-icons-common/system';
import Arc from './components/Arc';
import Heading from '../Heading';
import Tooltip from '../Tooltip';

import type { LoaderProps } from './Loader.types';
import {
  SIZES_TO_PIXELS_MAP,
  SIZE_TO_STROKE_WIDTH_MAP,
  SIZE_TO_ARC_ANGLE_MAP,
} from './Loader.constants';
import { st, classes } from './Loader.st.css';

const FULL_CIRCLE_ANGLE = 359;

const sizeToSuccessIconMap = {
  tiny: <ToggleOn />,
  small: <CircleLoaderCheckSmall />,
  medium: <CircleLoaderCheck />,
  large: <CircleLoaderCheck />,
};

const sizeToErrorIconMap = {
  tiny: <FormFieldError />,
  small: <FormFieldErrorSmall />,
  medium: <FormFieldError />,
  large: <FormFieldError />,
};

const Loader = ({
  size = 'medium',
  color = 'blue',
  text,
  status = 'loading',
  statusMessage,
  dataHook,
}: LoaderProps) => {
  const loader = useMemo(() => {
    const successIcon = sizeToSuccessIconMap[size];
    const errorIcon = sizeToErrorIconMap[size];
    const strokeWidth = SIZE_TO_STROKE_WIDTH_MAP[size];
    const sizeInPx = SIZES_TO_PIXELS_MAP[size];

    const shouldShowFullCircle = status !== 'loading';

    const lightArcAngle = shouldShowFullCircle
      ? FULL_CIRCLE_ANGLE
      : SIZE_TO_ARC_ANGLE_MAP[size].light;

    const darkArcAngle = shouldShowFullCircle
      ? FULL_CIRCLE_ANGLE
      : SIZE_TO_ARC_ANGLE_MAP[size].dark;

    return (
      <div
        className={classes.arcsContainer}
        style={{
          width: `${sizeInPx}px`,
          height: `${sizeInPx}px`,
        }}
      >
        <Arc
          angle={lightArcAngle}
          className={classes.lightArc}
          strokeWidth={strokeWidth}
          viewBoxSize={sizeInPx}
        />
        <Arc
          angle={darkArcAngle}
          className={classes.darkArc}
          strokeWidth={strokeWidth}
          viewBoxSize={sizeInPx}
        />
        {status !== 'loading' && (
          <div className={classes.statusIndicator}>
            {status === 'success' && successIcon}
            {status === 'error' && errorIcon}
          </div>
        )}
      </div>
    );
  }, [status, size]);

  return (
    <div
      className={st(classes.root, { size, color, status })}
      data-hook={dataHook}
      data-size={size}
      data-color={color}
      data-status={status}
    >
      {statusMessage ? (
        <Tooltip
          content={statusMessage}
          appendTo="window"
          dataHook="loader-tooltip"
        >
          {loader}
        </Tooltip>
      ) : (
        loader
      )}
      {size !== 'tiny' && text && (
        <div className={classes.text}>
          <Heading size="extraTiny" dataHook="loader-text">
            {text}
          </Heading>
        </div>
      )}
    </div>
  );
};

Loader.propTypes = {
  dataHook: PropTypes.string,
  size: PropTypes.oneOf(['tiny', 'small', 'medium', 'large']),
  color: PropTypes.oneOf(['blue', 'white']),
  text: PropTypes.node,
  status: PropTypes.oneOf(['loading', 'success', 'error']),
  statusMessage: PropTypes.string,
};

export default Loader;
